import { PCM16Decoder } from "./PCM16Decoder";

const SAMPLE_RATE = 24_000;
const MIN_BUFFER_SECONDS = 0.15;

export type VoiceModePlaybackStatus = "idle" | "playing" | "stopped";

export interface VoiceModePlaybackOptions {
  /** Fired whenever playback status changes. */
  onStatusChange?: (status: VoiceModePlaybackStatus) => void;
}

/**
 * Plays agent voice audio chunks using Web Audio, managing play/pause state.
 */
export class VoiceModePlayback {
  private readonly audioContext: AudioContext;
  private readonly onStatusChange: (status: VoiceModePlaybackStatus) => void;
  private readonly pcm16Decoder = new PCM16Decoder();

  private nextStartTime = 0;
  /** When false, `appendChunk` is a no-op until `start()` is called. */
  private isEnabled = true;
  private status: VoiceModePlaybackStatus = "idle";

  private activeSources = new Set<AudioBufferSourceNode>();

  constructor({ onStatusChange }: VoiceModePlaybackOptions = {}) {
    this.onStatusChange = onStatusChange ?? noop;
    this.audioContext = new AudioContext({
      latencyHint: "interactive",
      sampleRate: SAMPLE_RATE,
    });
  }

  /**
   * Returns the latest playback status.
   */
  public getStatus(): VoiceModePlaybackStatus {
    return this.status;
  }

  /**
   * Resumes accepting chunks after `stop()`. Also resumes a suspended AudioContext.
   */
  public async start(): Promise<void> {
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    this.isEnabled = true;
    this.syncStatus();
  }

  /**
   * Decodes and schedules a base64 PCM16 chunk. No-ops while status is `stopped`.
   */
  public async appendChunk(chunkBase64: string): Promise<void> {
    if (!this.isEnabled) {
      return;
    }

    await this.start();

    const samples = this.pcm16Decoder.decode(chunkBase64);

    if (samples.length === 0) {
      return;
    }

    const buffer = this.audioContext.createBuffer(
      1,
      samples.length,
      SAMPLE_RATE
    );

    buffer.copyToChannel(samples, 0);

    const source = this.audioContext.createBufferSource();

    source.buffer = buffer;
    source.connect(this.audioContext.destination);

    const now = this.audioContext.currentTime;

    // Schedule audio: if buffer underrun, add a small delay; otherwise, play immediately after the last chunk.
    if (this.nextStartTime < now) {
      this.nextStartTime = now + MIN_BUFFER_SECONDS;
    }

    source.start(this.nextStartTime);

    this.nextStartTime += buffer.duration;
    this.activeSources.add(source);
    this.syncStatus();

    source.addEventListener("ended", () => {
      this.activeSources.delete(source);
      source.disconnect();
      this.syncStatus();
    });
  }

  /**
   * Stops all scheduled/playing sources and rejects further chunks until `start()`.
   */
  public stop(): void {
    this.isEnabled = false;
    this.nextStartTime = 0;
    this.pcm16Decoder.reset();

    this.activeSources.forEach((source) => {
      try {
        source.stop();
      } catch {
        // The source may have already stopped.
      }

      source.disconnect();
    });

    this.activeSources.clear();
    this.syncStatus();
  }

  /**
   * Stops and clears all playing audio, then allows new audio chunks again.
   */
  public async restart(): Promise<void> {
    this.stop();
    await this.start();
  }

  /**
   * Returns whether any buffer source is currently playing.
   */
  public isPlaying(): boolean {
    return this.activeSources.size > 0;
  }

  /**
   * Returns how many seconds of audio remain on the schedule ahead of now.
   */
  public getBufferedDuration(): number {
    return Math.max(0, this.nextStartTime - this.audioContext.currentTime);
  }

  /**
   * Stops playback and closes the underlying AudioContext.
   */
  public async close(): Promise<void> {
    this.stop();
    await this.audioContext.close();
  }

  /**
   * Derives status from accepting/playing state and notifies on change.
   */
  private syncStatus(): void {
    const nextStatus = this.deriveStatus();

    if (this.status === nextStatus) {
      return;
    }

    this.status = nextStatus;
    this.onStatusChange(nextStatus);
  }

  /**
   * Maps internal flags to the public status union.
   */
  private deriveStatus(): VoiceModePlaybackStatus {
    if (!this.isEnabled) {
      return "stopped";
    }

    const hasScheduledAudio =
      this.activeSources.size > 0 ||
      this.nextStartTime > this.audioContext.currentTime;

    if (hasScheduledAudio) {
      return "playing";
    }

    return "idle";
  }
}

const noop = () => {};
